import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, Bug, Folder } from 'lucide-react';
import toast from 'react-hot-toast';

import { useAuth } from '@/contexts/AuthContext';
import { usePlatformAdmin } from '@/hooks/use-platform-admin';
import { apiClient, createAuthHeaders } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface SourceMetadata {
  project: {
    id: string;
    name: string;
    urlSlug: string;
    description: string | null;
    mode: string;
    agentVersion: number;
  };
  devContainer: {
    id: string;
    status: string;
    lastAccessedAt: string | null;
  } | null;
  ownerEmail: string | null;
  sourceWorkspaceId: string;
  backups: {
    workspacesTarGz: { exists: boolean; lastModifiedAt?: string; sizeBytes?: number };
    claudeSessionsTarGz: { exists: boolean; lastModifiedAt?: string; sizeBytes?: number };
    repoBundle: { exists: boolean; lastModifiedAt?: string; sizeBytes?: number };
  };
  connectionsSummary: Array<{ type: string; name: string }>;
  theme: { themeId: string; type: 'built-in' | 'custom' } | null;
}

const VALID_SOURCE_ENVS = ['sandbox2', 'production'] as const;
type SourceEnv = (typeof VALID_SOURCE_ENVS)[number];

export default function CloneFromEnv() {
  const { getIdToken } = useAuth();
  const { isPlatformAdmin, loading: adminLoading } = usePlatformAdmin();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const sourceEnv = searchParams.get('sourceEnv') as SourceEnv | null;
  const sourceProjectId = searchParams.get('sourceProjectId');

  const [meta, setMeta] = useState<SourceMetadata | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [cloning, setCloning] = useState(false);
  const [name, setName] = useState('');

  const paramsValid = useMemo(
    () =>
      !!sourceEnv &&
      VALID_SOURCE_ENVS.includes(sourceEnv) &&
      !!sourceProjectId,
    [sourceEnv, sourceProjectId],
  );

  useEffect(() => {
    if (!paramsValid) return;
    if (adminLoading) return;
    if (!isPlatformAdmin) {
      setPreviewError('You must be a platform admin to clone projects.');
      setPreviewLoading(false);
      return;
    }

    (async () => {
      console.log('[clone] preview fetch start', { sourceEnv, sourceProjectId });
      try {
        const token = await getIdToken();
        const response = await apiClient.get('/api/editor/projects/clone-from-env/preview', {
          params: { sourceEnv, sourceProjectId },
          headers: createAuthHeaders(token!),
        });
        setMeta(response.data);
        setName(`Clone of ${response.data.project.name}`);
        console.log('[clone] preview fetch done', {
          devContainerStatus: response.data.devContainer?.status,
          backups: response.data.backups,
        });
      } catch (err: any) {
        console.error('[clone] preview fetch failed', err?.response?.data ?? err);
        setPreviewError(
          err?.response?.data?.message ??
            err?.message ??
            'Failed to fetch source project metadata',
        );
      } finally {
        setPreviewLoading(false);
      }
    })();
  }, [paramsValid, adminLoading, isPlatformAdmin, sourceEnv, sourceProjectId, getIdToken]);

  const handleClone = async () => {
    if (!sourceEnv || !sourceProjectId) return;
    setCloning(true);
    console.log('[clone] submit start', { sourceEnv, sourceProjectId, name });
    try {
      const token = await getIdToken();
      const response = await apiClient.post(
        '/api/editor/projects/clone-from-env',
        { sourceEnv, sourceProjectId, name: name.trim() || undefined },
        { headers: createAuthHeaders(token!) },
      );
      const { project, warnings } = response.data;
      console.log('[clone] submit done', { newProjectId: project.id, warnings });

      if (warnings?.length) {
        for (const w of warnings) {
          toast(w.message, { icon: '⚠️', duration: 8000 });
        }
      } else {
        toast.success('Project cloned');
      }

      navigate(`/dashboard/editor/${project.id}/power?ws=${project.workspaceId}`);
    } catch (err: any) {
      console.error('[clone] submit failed', err?.response?.data ?? err);
      const data = err?.response?.data;
      toast.error(data?.message ?? 'Failed to clone project');
    } finally {
      setCloning(false);
    }
  };

  // Param / auth validation states
  if (!paramsValid) {
    return (
      <div className="max-w-2xl mx-auto p-8">
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6">
          <div className="flex items-center gap-2 text-destructive font-semibold mb-2">
            <AlertCircle size={18} /> Invalid clone link
          </div>
          <p className="text-sm text-muted-foreground">
            The clone link is missing or has invalid <code>sourceEnv</code> or{' '}
            <code>sourceProjectId</code> query parameters. Generate a fresh link
            from the source environment's project menu.
          </p>
        </div>
      </div>
    );
  }

  if (adminLoading || previewLoading) {
    return (
      <div className="max-w-2xl mx-auto p-8 flex items-center gap-3 text-muted-foreground">
        <Loader2 className="animate-spin" size={20} /> Loading source project…
      </div>
    );
  }

  if (previewError || !meta) {
    return (
      <div className="max-w-2xl mx-auto p-8">
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6">
          <div className="flex items-center gap-2 text-destructive font-semibold mb-2">
            <AlertCircle size={18} /> Cannot clone
          </div>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {previewError ?? 'Unknown error fetching source metadata.'}
          </p>
        </div>
      </div>
    );
  }

  // Decide whether the Clone button is disabled based on source state
  const dc = meta.devContainer;
  const tarPresent = meta.backups.workspacesTarGz.exists;
  const tarTakenAt = meta.backups.workspacesTarGz.lastModifiedAt;
  const transitioning = ['pausing', 'resuming', 'provisioning', 'starting', 'stopping'];

  let blocker: string | null = null;
  let warning: string | null = null;
  if (!dc) {
    blocker = 'Source has no dev container (Lite-only). Cross-env clone is Power-mode only.';
  } else if (transitioning.includes(dc.status)) {
    blocker = `Source dev container is ${dc.status}. Wait for it to settle, then refresh.`;
  } else if (dc.status === 'paused' && !tarPresent) {
    blocker = 'Source is paused but has no OCI backup. Cannot clone.';
  } else if (dc.status === 'running' && !tarPresent) {
    blocker = 'Source is running but has never been paused. Pause it once on the source env to create a backup, then refresh.';
  } else if (dc.status === 'running' && tarPresent) {
    warning = `Source is running. Cloning from snapshot taken ${formatRelative(tarTakenAt)} (changes after that won't be in this clone).`;
  }

  return (
    <div className="max-w-2xl mx-auto p-8 space-y-6">
      <div className="flex items-center gap-2 text-foreground">
        <Bug size={20} className="text-orange-500" />
        <h1 className="text-2xl font-semibold">Clone for debugging</h1>
      </div>

      <p className="text-sm text-muted-foreground">
        Cross-env clone from <strong>{sourceEnv}</strong> to your sandbox1 home
        workspace. Customer workspace, secrets, connections, Supabase project,
        deploys, and source-control links are not copied.
      </p>

      <section className="rounded-lg border border-border bg-card p-5 space-y-3">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          Source project
        </div>
        <div className="space-y-1.5">
          <div>
            <span className="text-muted-foreground">Name: </span>
            <span className="font-medium">{meta.project.name}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Slug: </span>
            <code className="text-xs">{meta.project.urlSlug}</code>
          </div>
          {meta.ownerEmail && (
            <div>
              <span className="text-muted-foreground">Owner: </span>
              <span>{meta.ownerEmail}</span>
            </div>
          )}
          {meta.devContainer && (
            <div>
              <span className="text-muted-foreground">Dev container: </span>
              <span>
                {meta.devContainer.status}
                {meta.devContainer.lastAccessedAt
                  ? ` (last active ${formatRelative(meta.devContainer.lastAccessedAt)})`
                  : ''}
              </span>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Workspace tar: </span>
            <span>
              {meta.backups.workspacesTarGz.exists
                ? `present (${formatBytes(meta.backups.workspacesTarGz.sizeBytes)}, taken ${formatRelative(tarTakenAt)})`
                : 'missing'}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Claude sessions tar: </span>
            <span>
              {meta.backups.claudeSessionsTarGz.exists
                ? `present (${formatBytes(meta.backups.claudeSessionsTarGz.sizeBytes)})`
                : 'absent — clone will start with no chat history'}
            </span>
          </div>
          {meta.connectionsSummary.length > 0 && (
            <div>
              <span className="text-muted-foreground">
                Original connections (NOT copied):
              </span>{' '}
              {meta.connectionsSummary.map((c) => `${c.name} (${c.type})`).join(', ')}
            </div>
          )}
        </div>
      </section>

      {warning && (
        <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/5 px-4 py-3 text-sm text-yellow-700 dark:text-yellow-300">
          ⚠️ {warning}
        </div>
      )}

      {blocker && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {blocker}
        </div>
      )}

      <section className="space-y-3">
        <Label htmlFor="clone-name">Clone name</Label>
        <Input
          id="clone-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={`Clone of ${meta.project.name}`}
        />
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Folder size={12} /> The clone will land at the root of your sandbox1
          home workspace.
        </p>
      </section>

      <div className="flex gap-3 justify-end">
        <Button
          variant="ghost"
          onClick={() => window.close()}
          disabled={cloning}
        >
          Cancel
        </Button>
        <Button onClick={handleClone} disabled={cloning || !!blocker}>
          {cloning ? (
            <>
              <Loader2 className="animate-spin mr-2" size={14} /> Cloning…
            </>
          ) : (
            'Clone'
          )}
        </Button>
      </div>
    </div>
  );
}

function formatBytes(n?: number): string {
  if (!n) return '?';
  const mb = n / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = n / 1024;
  return `${kb.toFixed(0)} KB`;
}

function formatRelative(iso?: string): string {
  if (!iso) return '?';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}
